import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, HomeIcon, AppWindowIcon, ShowerHeadIcon, BathIcon, LayersIcon, ChevronDownIcon } from 'lucide-react';
import { DaBellaQuoteForm } from './DaBellaQuoteForm';
interface DaBellaLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function DaBellaLandingPage({
  initialService,
  onClose
}: DaBellaLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <DaBellaQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-1073" />;
  }
  const services = [{
    id: 'roofing',
    name: 'Roofing',
    icon: <HomeIcon className="w-6 h-6" data-id="element-1074" />,
    desc: 'Roof replacement & repair'
  }, {
    id: 'windows',
    name: 'Windows',
    icon: <AppWindowIcon className="w-6 h-6" data-id="element-1075" />,
    desc: 'Window replacement'
  }, {
    id: 'siding',
    name: 'Siding',
    icon: <LayersIcon className="w-6 h-6" data-id="element-1076" />,
    desc: 'Exterior siding'
  }, {
    id: 'baths',
    name: 'Bathroom',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-1077" />,
    desc: 'Bath remodeling'
  }, {
    id: 'walk-in-tubs',
    name: 'Walk-in Tubs',
    icon: <BathIcon className="w-6 h-6" data-id="element-1078" />,
    desc: 'Accessible tubs'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  const heroImages: Record<string, string> = {
    roofing: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=1920&q=80',
    windows: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80',
    siding: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80',
    baths: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80',
    'walk-in-tubs': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80'
  };
  const heroImage = heroImages[selectedService] || 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80';
  const heroHeadlines: Record<string, string> = {
    roofing: 'A New Roof That Lasts a Lifetime',
    windows: 'Energy-Efficient Windows, Expertly Installed',
    siding: 'Premium Siding That Transforms Your Home',
    baths: 'Beautiful Bathrooms, Built for You',
    'walk-in-tubs': 'Safe & Accessible Walk-In Tubs'
  };
  const heroHeadline = initialService ? heroHeadlines[initialService] || 'Transform Your Home With DaBella' : 'Transform Your Home With DaBella';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1079">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-1080">
        <div className="flex flex-col items-center" data-id="element-1081">
          <div className="flex items-baseline gap-2 mb-1" data-id="element-1082">
            <img src="https://cdn.magicpatterns.com/uploads/qpzVzhypzNbXmPMfecWDrq/debella_logo.svg" alt="DaBella" className="h-12 md:h-14" data-id="element-1083" />
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-1084">
            Roofing &bull; Windows &bull; Siding &bull; Baths
          </span>
        </div>
        <div data-id="element-1085" />
      </header>

      {/* Offer Banner */}
      <section className="bg-[#1B5E20] py-4 md:py-6 border-t border-green-900" data-id="element-1086">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1087">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-1088">
            1 Year Deferred Interest**
          </h2>
          <p className="text-green-100 text-sm mt-1" data-id="element-1089">
            Limited time offer — schedule your free estimate today
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1090">
        <div className="absolute inset-0 z-0" data-id="element-1091">
          <img src={heroImage} alt="Home improvement" className="w-full h-full object-cover" data-id="element-1092" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-1093" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1094">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1095">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1096">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1097">
                {heroHeadline}
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1098">
                Get a free estimate from one of America's top-rated home
                improvement companies.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1099">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-1100">
                <h2 className="text-xl font-bold text-[#1B5E20] text-center mb-6" data-id="element-1101">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {/* Service dropdown (only if no initialService) */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1102">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-green-50 ${selectedService ? 'border-[#1B5E20]' : 'border-green-200'} ${dropdownOpen ? 'border-[#1B5E20] shadow-md' : 'hover:border-green-300'}`} data-id="element-1103">
                      <div className="flex items-center gap-3" data-id="element-1104">
                        {selectedService ? <>
                            <span className="text-[#1B5E20]" data-id="element-1105">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#1B5E20] text-lg" data-id="element-1106">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1107">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#1B5E20] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1108" />
                    </button>

                    <AnimatePresence data-id="element-1109">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-green-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1110">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-green-50 text-[#1B5E20]' : 'bg-white text-gray-700 hover:bg-green-50 hover:text-[#1B5E20]'} border-b border-green-100 last:border-b-0`} data-id="element-1111">
                              <span className={`${selectedService === svc.id ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-1112">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-1113">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#1B5E20] ml-auto" data-id="element-1114" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-1115">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#1B5E20]" data-id="element-1116">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-1117">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#1B5E20] focus:ring-1 focus:ring-[#1B5E20] transition-colors" maxLength={5} data-id="element-1118" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-1119">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-1120" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1121">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-1122">
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200" data-id="element-1123">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-1124" />
                    <span className="text-xs font-bold text-green-800 uppercase tracking-tight" data-id="element-1125">
                      Licensed & Insured
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-1126">
                    <div className="font-black text-blue-700 text-sm" data-id="element-1127">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-1128">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Accent Bar */}
      <div className="w-full h-3 bg-[#1B5E20]" data-id="element-1129" />

      {/* Awards Section */}
      <section className="py-16 bg-white border-b border-gray-100" data-id="element-1130">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1131">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-10" data-id="element-1132">
            A Trusted Award-Winning Home Improvement Experience
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8" data-id="element-1133">
            {['Qualified Remodeler TOP 500', 'GuildMaster Award', 'HomeAdvisor Top Rated', 'Angi Super Service 2024', 'Best Pick Reports Certified'].map((award, idx) => <div key={idx} className="bg-white border border-gray-200 shadow-sm rounded-xl px-6 py-4 flex items-center justify-center min-w-[160px]" data-id="element-1134">
                <span className="font-bold text-sm text-[#1B5E20] text-center leading-tight" data-id="element-1135">
                  {award}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-gray-50" data-id="element-1136">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1137">
          <div className="text-center mb-16" data-id="element-1138">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1139">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1140">
            {[{
            title: 'Choose Your Project',
            desc: "Whether it's a new roof, replacement windows, premium siding, or a bathroom transformation — DaBella has you covered with industry-leading products.",
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Schedule Your Free Estimate',
            desc: 'A DaBella project consultant will visit your home, assess your needs, review product options, and provide a transparent, no-obligation quote.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Expert Installation',
            desc: 'Our factory-trained, certified installers handle every detail. Backed by some of the strongest warranties in the industry for lasting peace of mind.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1141">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1142">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1143" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1144">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1145">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white" data-id="element-1146">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1147">
          <div className="text-center mb-12" data-id="element-1148">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1149">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1150">
            {[{
            name: 'David & Lisa M.',
            location: 'Portland, OR',
            text: 'DaBella replaced our 25-year-old roof with GAF Timberline HDZ shingles. The crew was professional, finished in two days, and the cleanup was spotless. Our home looks brand new!'
          }, {
            name: 'Sarah K.',
            location: 'Denver, CO',
            text: 'We had 12 windows replaced and the difference is incredible. Our energy bills dropped by 30% and the street noise is practically gone. The installation team was courteous and efficient.'
          }, {
            name: 'Robert T.',
            location: 'Dallas, TX',
            text: "The new siding completely transformed our home's curb appeal. DaBella walked us through every option and the final result exceeded our expectations. Highly recommend!"
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-1151">
                <div className="flex text-yellow-400 mb-4" data-id="element-1152">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-1153" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-1154">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div data-id="element-1155">
                  <p className="font-bold text-gray-900" data-id="element-1156">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-1157">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E20] py-16" data-id="element-1158">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1159">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1160">
            <div className="flex flex-col gap-8" data-id="element-1161">
              <div className="flex flex-col" data-id="element-1162">
                <img src="https://cdn.magicpatterns.com/uploads/qpzVzhypzNbXmPMfecWDrq/debella_logo.svg" alt="DaBella" className="h-8 brightness-0 invert mb-2" data-id="element-1163" />
                <span className="text-[10px] font-semibold text-white/90" data-id="element-1164">
                  Roofing &bull; Windows &bull; Siding &bull; Baths
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-1165">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1166">
              <div className="flex flex-col gap-4" data-id="element-1167">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1168">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1169">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1170">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1171">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1172">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1173">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-1174">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-1175">
              DaBella services are currently available in select markets across
              the United States. All estimates are free and no-obligation.
              Actual project costs may vary based on scope, materials, and local
              conditions. Licensed and insured. Contact us for full warranty
              details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}